import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18next from 'i18next';
import { applyTheme, buildThemeId, getTheme, applyFontSet } from '@/styles/themes';
import type { Brand, ColorMode, FontSet } from '@/styles/themes';
import { detectLocale } from '@/shared/i18n/locales';

export type Locale = 'en' | 'de' | 'fr' | 'it' | 'pt';
export type { Brand, ColorMode, FontSet };

interface PreferencesState {
  locale: Locale;
  colorMode: ColorMode;
  brand: Brand;
  fontSet: FontSet;
  displayLocale: string | undefined;

  setLocale: (locale: Locale) => void;
  setColorMode: (colorMode: ColorMode) => void;
  toggleColorMode: () => void;
  setBrand: (brand: Brand) => void;
  setFontSet: (fontSet: FontSet) => void;
  setDisplayLocale: (locale: string | undefined) => void;
}

function applyCurrentTheme(brand: Brand, colorMode: ColorMode, fontSet: FontSet): void {
  const id = buildThemeId(brand, colorMode);
  const base = getTheme(id);
  const themed = applyFontSet(base, fontSet);
  applyTheme(themed);
}

export const usePreferencesStore = create<PreferencesState>()(
  persist(
    (set, get) => ({
      // Brand-new visitor (no persisted state): default to the browser language so the
      // marketing/auth pages render in it. A persisted choice or the account language
      // (applied on login) overrides this.
      locale: detectLocale(),
      colorMode: 'light',
      brand: 'teal',
      fontSet: 'inter',
      displayLocale: undefined,

      setLocale(locale) {
        set({ locale });
        document.documentElement.lang = locale;
        i18next.changeLanguage(locale);
      },

      setColorMode(colorMode) {
        set({ colorMode });
        const { brand, fontSet } = get();
        applyCurrentTheme(brand, colorMode, fontSet);
      },

      toggleColorMode() {
        const next = get().colorMode === 'light' ? 'dark' : 'light';
        get().setColorMode(next);
      },

      setBrand(brand) {
        set({ brand });
        const { colorMode, fontSet } = get();
        applyCurrentTheme(brand, colorMode, fontSet);
      },

      setFontSet(fontSet) {
        set({ fontSet });
        const { brand, colorMode } = get();
        applyCurrentTheme(brand, colorMode, fontSet);
      },

      setDisplayLocale(locale) {
        set({ displayLocale: locale });
      },
    }),
    {
      name: 'casalary-preferences',
      version: 5,
      migrate(persistedState, version) {
        const s = ((persistedState ?? {}) as Record<string, unknown>);
        if (version < 1) {
          if (s['theme'] && !s['colorMode']) s['colorMode'] = s['theme'];
          if (!s['brand']) s['brand'] = 'teal';
          if (!s['fontSet']) s['fontSet'] = 'inter';
        }
        if (version < 2) {
          if (!('displayLocale' in s)) s['displayLocale'] = undefined;
        }
        if (version < 5) {
          // Tour flags retired with the Setup Guide (progress is now server-side).
          delete s['hasSeenOnboarding'];
          delete s['hasSeenTour'];
        }
        return s as unknown as PreferencesState;
      },
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        applyCurrentTheme(state.brand, state.colorMode, state.fontSet);
        document.documentElement.lang = state.locale;
        i18next.changeLanguage(state.locale);
      },
    }
  )
);

/** Returns the active display locale for date/number formatting. Falls back to OS locale. */
export function getDisplayLocale(): string {
  return usePreferencesStore.getState().displayLocale ?? navigator.language;
}
